import { BrowserRouter, Link, Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom';
import { lazy, Suspense, type ReactNode } from 'react';
import { AppShell } from './components/AppShell';
import { RouteErrorBoundary } from './components/RouteErrorBoundary';
import { AppVersionNotice } from './components/AppVersionNotice';
import { AuthProvider, useAuth } from './lib/auth';
import { DialogProvider } from './components/Dialog';
import { Login } from './pages/Login';
import { isAuthEntryPath, pathAfterLogin } from './lib/post-login-path';

const MarketIntelligence = lazy(() => import('./pages/MarketIntelligence').then((module) => ({ default: module.MarketIntelligence })));
const Operations = lazy(() => import('./pages/Operations').then((module) => ({ default: module.Operations })));
const Dashboard = lazy(() => import('./pages/Dashboard').then((module) => ({ default: module.Dashboard })));
const BookingDetail = lazy(() => import('./pages/Bookings').then((module) => ({ default: module.BookingDetail })));
const Bookings = lazy(() => import('./pages/Bookings').then((module) => ({ default: module.Bookings })));
const RentOlegEntry = lazy(() => import('./pages/RentOlegEntry').then((module) => ({ default: module.RentOlegEntry })));
const Tasks = lazy(() => import('./pages/Tasks').then((module) => ({ default: module.Tasks })));
const Notes = lazy(() => import('./pages/Notes').then((module) => ({ default: module.Notes })));
const Documents = lazy(() => import('./pages/Documents').then((module) => ({ default: module.Documents })));
const Calendar = lazy(() => import('./pages/Calendar').then((module) => ({ default: module.Calendar })));
const Settings = lazy(() => import('./pages/Settings').then((module) => ({ default: module.Settings })));
const Users = lazy(() => import('./pages/Users').then((module) => ({ default: module.Users })));
const Money = lazy(() => import('./pages/Money').then((module) => ({ default: module.Money })));
const Owners = lazy(() => import('./pages/Owners').then((module) => ({ default: module.Owners })));
const FinRoadmap = lazy(() => import('./pages/FinRoadmap').then((module) => ({ default: module.FinRoadmap })));
const Objects = lazy(() => import('./pages/Objects').then((module) => ({ default: module.Objects })));
const Decisions = lazy(() => import('./pages/Decisions').then((module) => ({ default: module.Decisions })));
const Waiting = lazy(() => import('./pages/Waiting').then((module) => ({ default: module.Waiting })));
const LeadDetail = lazy(() => import('./pages/Leads').then((module) => ({ default: module.LeadDetail })));
const Leads = lazy(() => import('./pages/Leads').then((module) => ({ default: module.Leads })));
const Tm30 = lazy(() => import('./pages/GuestServices').then((module) => ({ default: module.Tm30 })));
const Transfers = lazy(() => import('./pages/GuestServices').then((module) => ({ default: module.Transfers })));
const JackInbox = lazy(() => import('./pages/JackInbox').then((module) => ({ default: module.JackInbox })));
const GuestInbox = lazy(() => import('./pages/GuestInbox').then((module) => ({ default: module.GuestInbox })));
const Team = lazy(() => import('./pages/Team').then((module) => ({ default: module.Team })));
const GuestPropertyPassport = lazy(() => import('./pages/GuestPropertyPassport').then((module) => ({ default: module.GuestPropertyPassport })));

const Workspace = lazy(() => import('./pages/Workspace').then((module) => ({ default: module.Workspace })));
const ProjectWork = lazy(() => import('./pages/ProjectWork').then((module) => ({ default: module.ProjectWork })));
const ControlPlane = lazy(() => import('./pages/ControlPlane').then((module) => ({ default: module.ControlPlane })));
const OpsRunsList = lazy(() => import('./pages/OpsRuns').then((module) => ({ default: module.OpsRunsList })));
const OpsRunDetail = lazy(() => import('./pages/OpsRuns').then((module) => ({ default: module.OpsRunDetail })));

/** Старые ссылки ввода сохраняют параметры и ведут к проверке черновика. */
function OwnerComposer() {
  const { search, hash } = useLocation();
  return <Navigate to={{ pathname: '/workspace', search, hash }} replace />;
}

/** Неиспользуемая отдельная страница проектов остаётся закрыта. */
function OwnerHome() {
  return <Navigate to="/" replace />;
}

function JackOsRoute({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  return user?.role === 'admin' ? <>{children}</> : <Navigate to="/tasks" replace />;
}

function NotFound() {
  return (
    <main className="mx-auto max-w-lg px-6 py-16">
      <h1 className="text-xl font-semibold text-ink">Страница не найдена</h1>
      <p className="mt-3 text-sm text-muted">Такого адреса в Jack OS нет.</p>
      <div className="mt-5 flex gap-4 text-sm">
        <Link to="/" className="text-accent">На сегодня</Link>
        <Link to="/tasks" className="text-accent">К задачам</Link>
      </div>
    </main>
  );
}

function Forbidden() {
  return (
    <main className="mx-auto max-w-lg px-6 py-16">
      <h1 className="text-xl font-semibold text-ink">Нет доступа</h1>
      <p className="mt-3 text-sm text-muted">Этот раздел только для администратора.</p>
      <div className="mt-5 flex gap-4 text-sm">
        <Link to="/tasks" className="text-accent">К задачам</Link>
      </div>
    </main>
  );
}

/** Page failures stay below the shell; navigation resets only the failed section. */
function RouteSection({ children }: { children: ReactNode }) {
  const location = useLocation();
  return (
    <RouteErrorBoundary resetKey={location.key}>
      <Suspense fallback={<div className="p-8 text-sm text-muted" role="status">Загружаю раздел…</div>}>
        {children}
      </Suspense>
    </RouteErrorBoundary>
  );
}

function Gate() {
  const location = useLocation();
  const { user, loading, authError, refresh } = useAuth();

  if (loading) {
    return <div className="min-h-dvh bg-surface-2 p-8 text-sm text-muted" role="status">Проверяю вход…</div>;
  }
  if (!user && authError) {
    return (
      <div className="min-h-dvh bg-surface-2 p-8 text-ink" role="alert">
        <h1 className="text-lg font-semibold">Не удалось проверить вход</h1>
        <p className="mt-2 text-sm text-muted">Сессия не объявлена потерянной. {authError}</p>
        <button type="button" className="mt-4 min-h-11 rounded-lg bg-accent px-4 text-sm font-medium text-white" onClick={() => void refresh()}>Повторить проверку</button>
      </div>
    );
  }
  if (!user) return <Login />;
  // /login и /join не разделы. После входа адрес оставался на них и попадал в «не найдена».
  if (isAuthEntryPath(location.pathname)) {
    return <Navigate to={pathAfterLogin(location.search)} replace />;
  }

  return (
    <Routes>
      <Route path="rent-oleg" element={<RouteSection><RentOlegEntry /></RouteSection>} />
      <Route element={<AppShell />}>
        <Route element={<RouteSection><Outlet /></RouteSection>}>
        <Route index element={<JackOsRoute><Dashboard /></JackOsRoute>} />
        <Route path="work" element={<JackOsRoute><ProjectWork /></JackOsRoute>} />
        <Route path="workspace" element={<JackOsRoute><Workspace /></JackOsRoute>} />
        <Route path="inbox" element={<JackOsRoute><JackInbox /></JackOsRoute>} />
        <Route path="messages" element={<JackOsRoute><GuestInbox /></JackOsRoute>} />
        <Route path="tasks" element={<Tasks />} />
        <Route path="projects" element={<JackOsRoute><OwnerHome /></JackOsRoute>} />
        <Route path="calendar" element={<Calendar />} />
        <Route path="notes" element={<Notes />} />
        <Route path="documents" element={<JackOsRoute><Documents /></JackOsRoute>} />
        <Route path="decisions" element={<JackOsRoute><Decisions /></JackOsRoute>} />
        <Route path="waiting" element={<JackOsRoute><Waiting /></JackOsRoute>} />
        <Route path="control-plane" element={<JackOsRoute><ControlPlane /></JackOsRoute>} />
        <Route path="runs" element={<JackOsRoute><OpsRunsList /></JackOsRoute>} />
        <Route path="runs/:runId" element={<JackOsRoute><OpsRunDetail /></JackOsRoute>} />
        <Route path="leads" element={<JackOsRoute><Leads /></JackOsRoute>} />
        <Route path="leads/:leadId" element={<JackOsRoute><LeadDetail /></JackOsRoute>} />
        <Route path="tm30" element={<JackOsRoute><Tm30 /></JackOsRoute>} />
        <Route path="transfers" element={<JackOsRoute><Transfers /></JackOsRoute>} />
        <Route path="team" element={<JackOsRoute><Team /></JackOsRoute>} />
        <Route path="objects" element={<JackOsRoute><Objects /></JackOsRoute>} />
        <Route path="cleaning" element={<JackOsRoute><Operations /></JackOsRoute>} />
        <Route path="operations" element={<Navigate to="/cleaning" replace />} />
        <Route path="bookings" element={<JackOsRoute><Bookings /></JackOsRoute>} />
        <Route path="bookings/:bookingId" element={<JackOsRoute><BookingDetail /></JackOsRoute>} />
        <Route path="business" element={<JackOsRoute><OwnerComposer /></JackOsRoute>} />
        <Route path="market-intelligence" element={<JackOsRoute><MarketIntelligence /></JackOsRoute>} />
        <Route path="money" element={<JackOsRoute><Money /></JackOsRoute>} />
        <Route path="owners" element={<JackOsRoute><Owners /></JackOsRoute>} />
        <Route path="fin-roadmap" element={<JackOsRoute><FinRoadmap /></JackOsRoute>} />
        <Route path="settings" element={<Settings />} />
        <Route path="users" element={user.role === 'admin' ? <Users /> : <Forbidden />} />
        <Route path="*" element={<NotFound />} />
        </Route>
      </Route>
    </Routes>
  );
}

export function App() {
  const guestPassport = /^\/property\/[^/]+\/passport\/?$/.test(window.location.pathname);
  return (
    <BrowserRouter>
      <AppVersionNotice />
      {guestPassport ? (
        <RouteSection><GuestPropertyPassport /></RouteSection>
      ) : (
        <AuthProvider>
          <DialogProvider>
            <RouteErrorBoundary><Gate /></RouteErrorBoundary>
          </DialogProvider>
        </AuthProvider>
      )}
    </BrowserRouter>
  );
}
